import type { ContainerSelected } from "components/Environment/shared/BuildTypes";
import type { LaunchablePort } from "server";
import {
  checkPortConflicts,
  mergeDockerComposeNetworkServices,
  type NetworkConfiguration,
  type NetworkService,
} from "./networkServices";
import type { ManagedServiceStatus, RuntimeType } from "./types";

const VM_JUPYTER_SERVICE_ID = "vm-jupyter-notebook";
export const VM_JUPYTER_SERVICE: NetworkService = {
  id: VM_JUPYTER_SERVICE_ID,
  name: "jupyter-notebook",
  port: "8888",
  isJupyter: true,
  isFrontendOnly: true,
};

const FEATURED_CONTAINER_JUPYTER_SERVICE_ID =
  "featured-container-jupyter-notebook";
export const FEATURED_CONTAINER_JUPYTER_SERVICE: NetworkService = {
  id: FEATURED_CONTAINER_JUPYTER_SERVICE_ID,
  name: "jupyter",
  port: "8888",
  isJupyter: true,
  isFrontendOnly: true,
};

const DOCKER_COMPOSE_JUPYTER_SERVICE_ID = "docker-compose-jupyter-notebook";
export const DOCKER_COMPOSE_JUPYTER_SERVICE: NetworkService = {
  id: DOCKER_COMPOSE_JUPYTER_SERVICE_ID,
  name: "jupyter-notebook",
  port: "8888",
  isJupyter: true,
  isEnvironmentManaged: true,
  isFrontendOnly: true,
};

const KUBERNETES_DASHBOARD_SERVICE_ID = "kubernetes-dashboard";
export const KUBERNETES_DASHBOARD_SERVICE: NetworkService = {
  id: KUBERNETES_DASHBOARD_SERVICE_ID,
  name: "k8s-dashboard",
  port: "8001",
  isJupyter: true,
};

export function getManagedJupyterServiceForContainer(
  container: ContainerSelected,
): NetworkService | undefined {
  if (container.vmBuild?.k8s && !container.vmBuild.k8s.isDisabled) {
    return container.vmBuild.k8s.isDashboardEnabled
      ? KUBERNETES_DASHBOARD_SERVICE
      : undefined;
  }

  if (container.vmBuild) {
    return container.vmBuild.forceJupyterInstall
      ? VM_JUPYTER_SERVICE
      : undefined;
  }

  if (container.dockerCompose) {
    return container.dockerCompose.jupyterInstall
      ? DOCKER_COMPOSE_JUPYTER_SERVICE
      : undefined;
  }

  if (container.verbBuild) {
    return FEATURED_CONTAINER_JUPYTER_SERVICE;
  }

  return undefined;
}

export const getInitialNetworkServices = (
  ports: LaunchablePort[],
  initialContainer?: ContainerSelected,
): NetworkService[] => {
  const managedServiceName = initialContainer
    ? getManagedJupyterServiceForContainer(initialContainer)?.name.toLowerCase()
    : undefined;

  return ports.map((port, index) => {
    const normalizedName = port.name.trim().toLowerCase();

    return {
      id: `existing-service-${index}`,
      name: port.name,
      port: port.port,
      isJupyter: normalizedName === managedServiceName,
      isEnvironmentManaged: port.labels?.["managed-by"] === "docker-compose",
      isCTA: port.labels?.["cta-enabled"] === "true",
      ctaLabel: port.labels?.["cta-value"],
    };
  });
};

export const getInitialManagedServiceStatus = ({
  initialRuntimeType,
  initialContainer,
  isEditMode,
}: {
  initialRuntimeType: RuntimeType;
  initialContainer?: ContainerSelected;
  isEditMode: boolean;
}): ManagedServiceStatus => {
  if (initialRuntimeType === "VM") {
    if (!isEditMode && !initialContainer) {
      return "configured";
    }

    return initialContainer?.vmBuild?.forceJupyterInstall
      ? "configured"
      : "not-configured";
  }

  if (initialRuntimeType === "kubernetes") {
    return initialContainer?.vmBuild?.k8s?.isDashboardEnabled
      ? "configured"
      : "not-configured";
  }

  return "not-configured";
};

export const getInitialNetworkConfiguration = ({
  savedConfiguration,
  initialContainer,
  isEditMode,
}: {
  savedConfiguration: NetworkConfiguration;
  initialContainer: ContainerSelected;
  isEditMode: boolean;
}): NetworkConfiguration => {
  if (isEditMode) {
    return savedConfiguration;
  }

  return reconcileNetworkConfiguration(savedConfiguration, initialContainer);
};

export const syncEnvironmentJupyterService = (
  services: NetworkService[],
  managedService?: NetworkService,
): NetworkService[] => {
  const servicesWithoutManagedJupyter = services.filter(
    (service) =>
      service.id !== VM_JUPYTER_SERVICE_ID &&
      service.id !== FEATURED_CONTAINER_JUPYTER_SERVICE_ID &&
      service.id !== DOCKER_COMPOSE_JUPYTER_SERVICE_ID &&
      service.id !== KUBERNETES_DASHBOARD_SERVICE_ID,
  );

  if (!managedService) {
    return servicesWithoutManagedJupyter.filter(
      (service) => !service.isJupyter,
    );
  }

  return [
    ...servicesWithoutManagedJupyter.filter((service) => !service.isJupyter),
    managedService,
  ];
};

export const reconcileNetworkConfiguration = (
  current: NetworkConfiguration,
  container: ContainerSelected,
): NetworkConfiguration => {
  const servicesWithManagedLink = syncEnvironmentJupyterService(
    current.services,
    getManagedJupyterServiceForContainer(container),
  );
  let services: NetworkService[];
  let configurationError: string | undefined;

  if (container.dockerCompose) {
    const composeYaml =
      container.dockerCompose.parsedYaml || container.dockerCompose.yamlString;

    if (composeYaml) {
      try {
        services = mergeDockerComposeNetworkServices(
          servicesWithManagedLink,
          composeYaml,
          !!container.dockerCompose.jupyterInstall,
        );
      } catch {
        services = servicesWithManagedLink.filter(
          (service) => !service.isEnvironmentManaged || service.isJupyter,
        );
        configurationError = "Invalid Docker Compose YAML format.";
      }
    } else {
      services = servicesWithManagedLink;
    }
  } else {
    services = servicesWithManagedLink.filter(
      (service) => !service.isEnvironmentManaged,
    );
  }

  const checked = checkPortConflicts(services, current.firewallRules);
  return {
    services: checked.services,
    firewallRules: checked.firewallRules,
    ...(configurationError ? { configurationError } : {}),
  };
};
